// src/contexts/ToastContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import './Toast.css';

// Create a context for toasts
const ToastContext = createContext();

// Custom hook to use the toast context
export const useToast = () => useContext(ToastContext);

// Toast provider component
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Function to add a new toast
  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    // Generate a more unique ID by combining timestamp with a random number
    const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Add new toast to the array
    setToasts(prevToasts => [...prevToasts, { id, message, type }]);
    
    // Remove toast after duration
    setTimeout(() => {
      setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
    }, duration);
    
    return id;
  }, []);
  
  // Helper functions for different toast types
  const showError = useCallback((message, duration) => 
    showToast(message, 'error', duration), [showToast]);
  
  const showSuccess = useCallback((message, duration) => 
    showToast(message, 'success', duration), [showToast]);
    
  const showInfo = useCallback((message, duration) => 
    showToast(message, 'info', duration), [showToast]);
    
  const showWarning = useCallback((message, duration) => 
    showToast(message, 'warning', duration), [showToast]);
  
  // Function to remove a toast by id
  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ 
      showToast, showError, showSuccess, showInfo, showWarning, removeToast
    }}>
      {children}
      
      {/* Toast container that shows all current toasts */}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map(toast => (
            <div 
              key={toast.id} 
              className={`toast toast-${toast.type}`}
              onClick={() => removeToast(toast.id)}
            >
              <div className="toast-content">
                {toast.type === 'error' && <i className="fa-solid fa-circle-exclamation"></i>}
                {toast.type === 'success' && <i className="fa-solid fa-check-circle"></i>}
                {toast.type === 'info' && <i className="fa-solid fa-info-circle"></i>}
                {toast.type === 'warning' && <i className="fa-solid fa-triangle-exclamation"></i>}
                <span>{toast.message}</span>
              </div>
              <button className="toast-close" onClick={(e) => {
                e.stopPropagation();
                removeToast(toast.id);
              }}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
} 